import { createDefaultLogger, LogLevels } from "@/types/console-logger";
const logger = createDefaultLogger( LogLevels.DEBUG );

import { goFetch } from "@/utils/network-utils";

import { cache, sectorHistory, syncState } from "@/services/cache";
import { sectorHistoryFullCheckDateKey, sectorHistoryFullCheckDueKey, sectorHistorySyncKey } from "@/types/products/product-keys";

import {
    BpiStatus,
    NormalizedPersistedSectorsState,
    NormalizedSectorMarketState,
    SectorAlert,
    SectorMarketState
} from "@tmi/types/entities";
import { CompositionContext } from "@/services/data/sync-supervisor";
import { DateTime } from "luxon";
import {
    SectorAlertApiResponse,
    SectorHistoryApiResponse,
    SectorHistoryForDate,
    SectorStatsApiResponse
} from "@tmi/types/api/market-data";
import { SectorAlertTypes } from "@tmi/types/enums/sector-alerts";
import { Tuple } from "@tmi/types/adts";
import { normalizeColumn, normalizeSignal } from "@/services/data/sync/normalize-column";
import { Forbidden, Unauthorized } from "@tmi/types/api/responses";
import type { SyncPassOutcome } from "@/services/data/sync/content-sync";

type SectorHistoryMetaApiResponse = {
    sectorHistoryMetaData: {
        dts: string[]
    }
}

/**
 * tmi-ck2 C4: the body of `POST sentient/sector-history-for-dates`, which mirrors the GraphQL
 * variables of the persisted document it replaces one-for-one. The twin binds this object
 * DIRECTLY — there is no `{ input: … }` or `{ variables: … }` envelope, and a wrapper would bind
 * an object with no matching properties, leaving `dates` empty and short-circuiting the server to
 * an empty result with a 200.
 *
 * `skip` is a PAGE INDEX, not a row offset, on both transports: the resolver assigns it to a local
 * named `page`. This caller has always sent 0 and paged by slicing `missingDates` itself, so the
 * value stays 0 — do not "fix" it into `page * pageSize`.
 */
type SectorHistoryForDatesRequest = {
    skip: number,
    limit: number,
    marketDataFilter: {
        dates: string[]
    }
}

async function fetchSectorHistoryKeys(): Promise< string[] > {
    let allSectorHistoryDates: string[] = [];

    // Timing - testing to see how expensive each method is
    const t0: number = performance.now();

    let sectorHistoryMetaPayload: string[] = [];
    // tmi-ck2 C4: `sectorHistoryMetaData` (was persisted query d271528e71bf59d763917ba4acd14273).
    // The twin takes no arguments and carries no .RequireAuthorization, so this stays the one
    // sentient read in the sync path that runs before the visitor has a token.
    const response =
        await goFetch< SectorHistoryMetaApiResponse, never >( {
            serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
            endpoint: 'sentient/sector-history-meta-data',
            verbOverride: 'GET'
        } );

    if ( response?.data?.sectorHistoryMetaData?.dts ) {
        sectorHistoryMetaPayload = response.data.sectorHistoryMetaData.dts;
        const t1: number = performance.now();
        logger.info( `Fetching ${ sectorHistoryMetaPayload.length } sector history keys took ${ t1 - t0 } ms` );

        allSectorHistoryDates = [ ...sectorHistoryMetaPayload ];
    }

    return allSectorHistoryDates;
}

/**
 * DP-10 (tmi-e7w.10): how many of the newest dates every pass fetches again although they are cached. The
 * writer rewrites the whole history on each run, and its newest sessions are computed over a population that
 * fills in with late price bars over the following ~15 sessions, so their values (and, through the P&F replay,
 * later columns and signals) can still change. 40 is one request page (Joel's ruling).
 */
export const REVISABLE_DATE_COUNT = 40;

/**
 * DP-10: the dates a pass fetches, in date order: those missing from the cache, the newest
 * REVISABLE_DATE_COUNT, and any cached day whose BPI, %30 or %10 column the server had not filled yet
 * (normalized to '---'), at any age, until it is filled.
 */
export function datesToRefresh( serverDates: readonly string[], cached: ReadonlyMap< string, NormalizedPersistedSectorsState > ): string[] {
    const chronological = [ ...serverDates ].sort();
    const newest = new Set( chronological.slice( -REVISABLE_DATE_COUNT ) );

    return chronological.filter( dt => {
        const day = cached.get( dt );
        return !day || newest.has( dt ) || hasUnknownBreadthColumn( day );
    } );
}

/**
 * DP-10: how long after a new market date first appears the whole history is checked again. A writer run can
 * restate years of history (the 2026-09-14 reload changed five sectors back to 2019, with 330 BPI column flips
 * older than the window). The server caches each date's history for 30 minutes (TMI.Core 4.0.13
 * CacheDurations.InfrequentlyChanged), so a response cached before the writer finished can be served until
 * 30 minutes after the new date is visible; the margin covers the pass throttle.
 */
export const FULL_CHECK_DELAY_MS = 35 * 60 * 1000;

/**
 * DP-10: whether this pass re-fetches the whole history. Once per market date, FULL_CHECK_DELAY_MS after the
 * date first appears. The check counts as done as soon as it starts, so a failing server cannot turn every
 * pass into a full download; a check it cuts short waits for the next market date.
 */
async function isFullCheckDue( serverDates: readonly string[] ): Promise< boolean > {
    const latest = [ ...serverDates ].sort().at( -1 )!;
    const now = DateTime.utc().toMillis();

    if ( await syncState.get< string >( sectorHistoryFullCheckDateKey ) !== latest ) {
        await syncState.put( sectorHistoryFullCheckDateKey, latest );
        await syncState.put( sectorHistoryFullCheckDueKey, now + FULL_CHECK_DELAY_MS );
        return false;
    }

    const due = await syncState.get< number >( sectorHistoryFullCheckDueKey ) ?? 0;
    if ( due === 0 || now < due ) {
        return false;
    }

    await syncState.put( sectorHistoryFullCheckDueKey, 0 );
    return true;
}

function hasUnknownBreadthColumn( day: NormalizedPersistedSectorsState ): boolean {
    return day.sectors.some( s => s.bpi.col === '---' || s.p30.col === '---' || s.p10.col === '---' );
}

/**
 * DP-10: whether a refetched day holds what the cache already has. EW index and RS levels are left out: they
 * are compounded from the first day of whichever batch fetched the day (DP-53), and the store recompounds them
 * over the whole history on load.
 */
function sameServerValues( cached: NormalizedPersistedSectorsState | undefined, fetched: NormalizedPersistedSectorsState ): boolean {
    return !!cached && JSON.stringify( withoutBatchLevels( cached ) ) === JSON.stringify( withoutBatchLevels( fetched ) );
}

function withoutBatchLevels( day: NormalizedPersistedSectorsState ) {
    return [ ...day.sectors ]
        .sort( ( s1, s2 ) => s1.sectorTmiId - s2.sectorTmiId )
        .map( s => ( {
            ...s,
            idx: { ...s.idx, val: 0, delta: 0 },
            rs: { ...s.rs, val: 0 },
            alerts: s.alerts?.map( alert => ( { ...alert, rs: { ...alert.rs, val: 0 } } ) )
        } ) );
}

/**
 * DP-21 (tmi-e7w.21): how a sync pass retries one history page. goFetch throws its typed errors (500, 404, 400,
 * ApiError) and returns null for a timeout or a network failure. A cold history is ~1,934 dates, 49 pages, and one
 * such failure used to cost the pass: a throw discarded every page already received, a null skipped 40 dates. A
 * page is now tried `attempts` times, `retryDelayMs` apart, and then skipped; its dates stay missing, so the next
 * pass fetches them. Unauthorized and Forbidden are never retried.
 */
export type PageRetryPolicy = {
    /** Tries per page, the first one included. */
    attempts: number,
    retryDelayMs: number,
    /** Specs replace the timer. */
    wait?: ( ms: number ) => Promise< void >
};

export const PAGE_RETRY_POLICY: Readonly< PageRetryPolicy > = { attempts: 3, retryDelayMs: 1000 };

export type RefreshFromServerOptions = {
    /** Without a policy every page is tried once and a thrown error escapes, as for the store's single-date fetch. */
    retry?: PageRetryPolicy,
    /** Called with each page's days, statuses and alerts settled, before the next page is requested. */
    onPage?: ( days: NormalizedPersistedSectorsState[] ) => Promise< void >,
    /** Called for a page that never arrived; its dates stay missing, so the pass did not bring the cache up to date. */
    onPageLost?: ( pagedDates: string[] ) => void
};

export type SectorHistoryPassResult = {
    /** Whether the pass wrote anything (since `onFirstFill`, when it was called). */
    updated: boolean,
    /** UX roadmap #58: a failure used to be caught and only logged, so nothing could tell the member. */
    outcome: SyncPassOutcome
};

export class SectorHistorySyncCoordinator {
    readonly #pageRetry: PageRetryPolicy;

    constructor( pageRetry: PageRetryPolicy = PAGE_RETRY_POLICY ) {
        this.#pageRetry = pageRetry;
    }

    /**
     * @param onFirstFill DP-21 (Joel's ruling): called once on a cold cache, after the newest page is written and before
     *        the rest of the history is requested, so the sector pages fill early. The return value then covers only
     *        what was written after it.
     * @param force UX roadmap #107: a member's refresh runs inside an unexpired cooldown.
     * @returns whether the pass wrote anything (since `onFirstFill`, when it was called), and how it ended. A failure
     *          is caught and reported as 'failed', never thrown.
     */
    public async patchRepository( authToken: string, onFirstFill?: () => void, force: boolean = false ): Promise< SectorHistoryPassResult > {
        let updated = false;
        let outcome: SyncPassOutcome = 'skipped';

        try {
            const t0: number = performance.now();

            await cache.ready();
            // Check for the next refresh interval
            const syncThresholdInMillis = await syncState.get< number >( sectorHistorySyncKey );
            if ( force || null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
                const sectorHistoryDates: string[] = await fetchSectorHistoryKeys();

                if ( 0 === sectorHistoryDates.length ) {
                    // The history is never legitimately empty: the key request failed (goFetch returns null offline).
                    const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                    await syncState.put( sectorHistorySyncKey, nextSyncThresholdInMillis );
                    return { updated, outcome: 'failed' };
                }

                // Set throttle after successful fetch
                const nextSyncThresholdInMillis = DateTime.utc().plus( { minutes: 5 } ).toMillis();
                await syncState.put( sectorHistorySyncKey, nextSyncThresholdInMillis );

                // Load items currently in cache
                const datesInCache = new Map<string, NormalizedPersistedSectorsState>();
                if ( !await sectorHistory.isEmpty() ) {
                    for ( const item of await sectorHistory.all() ) {
                        if ( item?.sectors?.length > 0 ) {
                            datesInCache.set( item.dt, item );
                        }
                    }
                }

                // DP-09: bring cached statuses in line with the writer's rule (this corrects
                // caches built by the old daily rule once, and afterwards finds nothing).
                // A day whose BPI Status alert changed bias is fetched again.
                const { changed, refetchDates } = reconcileBpiStatuses( [ ...datesInCache.values() ] );
                if ( changed.length > 0 ) {
                    updated = true;
                    await sectorHistory.putMany( changed );
                }
                // DP-20 R1: the day stays in `datesInCache`, so its cached values remain a fold neighbor (and in the
                // restatement below) until a refetch returns it. Its stale alert is never written from here: the
                // reconcile left it out of `changed`, and any status change it gets again goes to refetch.
                const mustRefetch = new Set( refetchDates );
                const datesToFetch = await isFullCheckDue( sectorHistoryDates )
                    ? [ ...sectorHistoryDates ].sort()
                    : datesToRefresh( sectorHistoryDates, new Map( [ ...datesInCache ].filter( ( [ dt ] ) => !mustRefetch.has( dt ) ) ) );
                let pagesLost = false;
                if ( datesToFetch.length > 0 ) {
                    let revisedAny = false;
                    let stoppedBy: { error: unknown } | null = null;

                    const fetchAndWrite = ( dates: string[] ) =>
                        refreshFromServer( authToken, dates, undefined, [ ...datesInCache.values() ], {
                            retry: this.#pageRetry,
                            // DP-21: each page is written as it arrives, so a later failure cannot discard it.
                            // DP-10: most refetched days come back as cached; write only what the server revised.
                            onPage: async days => {
                                const revised = days.filter( day => day.sectors?.length > 0 && (
                                    mustRefetch.has( day.dt ) || !sameServerValues( datesInCache.get( day.dt ), day )
                                ) );
                                if ( revised.length > 0 ) {
                                    await sectorHistory.putMany( revised );
                                    updated = true;
                                    revisedAny = true;
                                    revised.forEach( day => datesInCache.set( day.dt, day ) );
                                }
                            },
                            onPageLost: () => {
                                pagesLost = true;
                            }
                        } );

                    try {
                        // DP-21 (Joel's ruling): a cold cache fetches the newest page first and reports it, then the
                        // rest oldest first. The newest page is folded without the older history, so its statuses are
                        // provisional (on the production mirror, 761 of 1,800 sector-days had no status yet); the
                        // restatement below corrects them. Its values, columns and signals are final.
                        const newest = datesInCache.size === 0 ? datesToFetch.slice( -REVISABLE_DATE_COUNT ) : [];
                        const older = datesToFetch.slice( 0, datesToFetch.length - newest.length );
                        if ( newest.length > 0 && older.length > 0 ) {
                            await fetchAndWrite( newest );
                            if ( updated && onFirstFill ) {
                                onFirstFill();
                                updated = false;
                            }
                            await fetchAndWrite( older );
                        } else {
                            await fetchAndWrite( datesToFetch );
                        }
                    } catch ( err: unknown ) {
                        stoppedBy = { error: err };
                    }

                    // A revised day can change the status of cached days after it that were not refetched, and a cold
                    // pass's newest page holds provisional statuses. The revised days are already written, so this
                    // runs even when a page stopped the pass.
                    const { changed: restated, refetchDates: rebias } = revisedAny
                        ? reconcileBpiStatuses( [ ...datesInCache.values() ] )
                        : { changed: [], refetchDates: [] };
                    if ( restated.length > 0 ) {
                        await sectorHistory.putMany( restated );
                        updated = true;
                    }

                    if ( stoppedBy ) {
                        throw stoppedBy.error;
                    }

                    // A restated day whose BPI Status alert changed bias holds the other performer list: fetch it again
                    // now, folded against the whole history. The fetched alert differs from the cached one, so the day is
                    // written. If this fails, the next pass's reconcile sends it back.
                    if ( rebias.length > 0 ) {
                        await fetchAndWrite( rebias );
                    }

                    const t4: number = performance.now();
                    logger.info( `[ sector-history-sync-coordinator ] Persisting all missing sector history took ${ t4 - t0 } ms` );
                }
                // A skipped page is refetched by the next pass; until then the history is not current.
                outcome = pagesLost ? 'failed' : 'ok';
            }
        } catch ( err: any ) {
            logger.error( '[ sector-history-sync-coordinator ] Sync failed:', err );
            outcome = 'failed';
        }

        return { updated, outcome };
    }
}

const sectorAlertTypeKeyValues: readonly ( readonly [ number, string ] )[] = [
    [ SectorAlertTypes.rs_signal, 'Relative Strength Signal' ],
    [ SectorAlertTypes.rs_direction, 'Relative Strength Direction' ],
    [ SectorAlertTypes.bpi_signal, 'BPI Signal' ],
    [ SectorAlertTypes.bpi_direction, 'BPI Direction' ],
    [ SectorAlertTypes.bpi_status, 'BPI Status' ],
    [ SectorAlertTypes.wk_30_signal, '%30 Signal' ],
    [ SectorAlertTypes.wk_30_direction, '%30 Direction' ],
    [ SectorAlertTypes.wk_10_signal, '%10 Signal' ],
    [ SectorAlertTypes.wk_10_direction, '%10 Direction' ]
];
export const sectorAlertTypeKeyToDisplayValueMapper: Map<number, string> = new Map( sectorAlertTypeKeyValues );

// NOTE: in its current form, there is no "last updated" date b/c the entire history (back to 2020-01-01) is rebuilt each time it is ingested
// b/c we don't know if II has changed values or not
// `seedContext` is each sector's status state before the first fetched day, for callers without the
// cached days themselves; `priorHistory` is the cached days, which the status fold interleaves by date.
const SECTOR_HISTORY_PAGE_SIZE = 40;

function waitFor( ms: number ): Promise< void > {
    return new Promise( resolve => setTimeout( resolve, ms ) );
}

/**
 * One page of the history query. With a retry policy (DP-21) a failed attempt, thrown or null, is tried again
 * and the page finally answers null; Unauthorized and Forbidden always escape. Without one, a single attempt
 * behaves as goFetch does.
 */
async function fetchHistoryPage( authToken: string, pagedDates: string[], retry?: PageRetryPolicy ): Promise< { data: SectorHistoryApiResponse } | null > {
    const attempts = Math.max( 1, retry?.attempts ?? 1 );

    for ( let attempt = 1; ; attempt++ ) {
        try {
            // tmi-ck2 C4: `sectorHistoryForDates` (was persisted query a3532ccad48cd1cd1e1ad86742cf1fdb).
            // The one POST on the sentient surface — the operation carries a structured filter object,
            // so conventions §2 keeps it a body rather than a query string.
            const response = await goFetch< SectorHistoryApiResponse, SectorHistoryForDatesRequest >( {
                serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
                endpoint: 'sentient/sector-history-for-dates',
                payload: {
                    skip: 0,
                    limit: SECTOR_HISTORY_PAGE_SIZE,
                    marketDataFilter: {
                        dates: pagedDates
                    }
                },
                authToken: authToken
            } );
            if ( response ) {
                return response;
            }
            logger.warn( `[ sector-history-sync-coordinator ] History page starting ${ pagedDates[ 0 ] } returned nothing (attempt ${ attempt } of ${ attempts })` );
        } catch ( err: unknown ) {
            if ( !retry || err instanceof Unauthorized || err instanceof Forbidden ) {
                throw err;
            }
            logger.warn( `[ sector-history-sync-coordinator ] History page starting ${ pagedDates[ 0 ] } failed (attempt ${ attempt } of ${ attempts })`, err );
        }

        if ( attempt >= attempts ) {
            return null;
        }
        await ( retry?.wait ?? waitFor )( retry?.retryDelayMs ?? 0 );
    }
}

export async function refreshFromServer(
    authToken: string,
    missingDates: string[],
    seedContext?: Map< number, BpiStatusContext >,
    priorHistory: readonly NormalizedPersistedSectorsState[] = [],
    options: RefreshFromServerOptions = {}
): Promise<NormalizedPersistedSectorsState[]> {
    const entities: NormalizedPersistedSectorsState[] = [];

    // tmi-ck2 C4 signed-out guard. `sentient/sector-history-for-dates` carries
    // .RequireAuthorization, so a token-less call is a guaranteed 401 — and under REST goFetch
    // REJECTS a 401 rather than resolving it, where the GraphQL transport returned HTTP 200 with
    // an `errors` array that the caller quietly treated as "no data". Without this guard a
    // signed-out sync sweep would throw out of patchRepository's try block on every pass. The
    // metadata read above is anonymous and deliberately runs first regardless.
    if ( !authToken ) {
        logger.debug( '[ sector-history-sync-coordinator ] No auth token — skipping the sector history fetch.' );
        return entities;
    }

    const t0: number = performance.now();
    let consecutiveFailures: number = 0;
    const maxConsecutiveFailures: number = 2; // Stop after 2 consecutive failed pages to prevent cascading failures

    // DP-21: pages go out in date order and each is settled before the next, so a page's statuses and EW/RS levels
    // depend only on days already folded: the cached neighbors up to its last date and the pages before it.
    const requestedDates = [ ...missingDates ].sort();
    const neighbors = [ ...priorHistory ].sort( ( d1, d2 ) => d1.dt.localeCompare( d2.dt ) );
    let nextNeighbor = 0;
    let statusContexts = new Map< number, BpiStatusContext >( seedContext ?? [] );
    const levels = createLogReturnLevels();

    for ( let startingIndex = 0; startingIndex < requestedDates.length; startingIndex += SECTOR_HISTORY_PAGE_SIZE ) {
        const pagedDates = requestedDates.slice( startingIndex, startingIndex + SECTOR_HISTORY_PAGE_SIZE );
        const pageEntities: NormalizedPersistedSectorsState[] = [];

        // Raw alerts + performer lists held back until the status pass below has
        // populated bpi.stat, keyed by `${ dt }|${ sector_id }`.
        const pendingAlerts = new Map< string, { alerts: SectorAlert[], performers: TopAndBottomPerformers } >();

        const response = await fetchHistoryPage( authToken, pagedDates, options.retry );

        const historyItems = response?.data;
        if ( historyItems && ( historyItems.sectorHistoryForDates?.items?.length ?? 0 ) > 0 ) {
            let sectorEntities: NormalizedSectorMarketState[] = [];
            historyItems.sectorHistoryForDates.items
                .forEach( ( i: SectorHistoryForDate ) => {

                    const dateOfAnalysis: string = i.dt;
                    i.history.forEach( ( h: SectorStatsApiResponse ) => {
                        const performers: TopAndBottomPerformers = {
                            top_etfs: [],
                            top_stocks: [],
                            bot_etfs: [],
                            bot_stocks: [],
                        };

                        const alerts: SectorAlert[] = h.alerts?.kind.length > 0 ?
                            [ ...( h.alerts.kind.map( ( kind: number ) =>
                                ( {
                                    id: '',
                                    dt: i.dt,
                                    sector_id: -1,
                                    typeId: kind,
                                    typeName: sectorAlertTypeKeyToDisplayValueMapper.get( kind )!,
                                    bullish: false,
                                    idx: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    bpi: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        stat: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: '',
                                        stat_chg_dt: ''
                                    },
                                    p30: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    p10: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    rs: {
                                        rnk: -1,
                                        log_rtn: 0,
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    stocks: [],
                                    etfs: []
                                } )
                            ) ) ]
                            : [];

                        if ( h.alerts?.kind.length > 0 ) {
                            const sectorPerformers: SectorAlertApiResponse = h.alerts;

                            // Mapping the top stocks
                            sectorPerformers.top_stock_1 ? performers.top_stocks.push( sectorPerformers.top_stock_1 ) : '';
                            sectorPerformers.top_stock_2 ? performers.top_stocks.push( sectorPerformers.top_stock_2 ) : '';
                            sectorPerformers.top_stock_3 ? performers.top_stocks.push( sectorPerformers.top_stock_3 ) : '';

                            // Mapping the top etfs
                            sectorPerformers.top_etf_1 ? performers.top_etfs.push( sectorPerformers.top_etf_1 ) : '';
                            sectorPerformers.top_etf_2 ? performers.top_etfs.push( sectorPerformers.top_etf_2 ) : '';
                            sectorPerformers.top_etf_3 ? performers.top_etfs.push( sectorPerformers.top_etf_3 ) : '';

                            // Mapping the bottom stocks
                            sectorPerformers.bot_stock_1 ? performers.bot_stocks.push( sectorPerformers.bot_stock_1 ) : '';
                            sectorPerformers.bot_stock_2 ? performers.bot_stocks.push( sectorPerformers.bot_stock_2 ) : '';
                            sectorPerformers.bot_stock_3 ? performers.bot_stocks.push( sectorPerformers.bot_stock_3 ) : '';

                            // Mapping the top etfs
                            sectorPerformers.bot_etf_1 ? performers.bot_etfs.push( sectorPerformers.bot_etf_1 ) : '';
                            sectorPerformers.bot_etf_2 ? performers.bot_etfs.push( sectorPerformers.bot_etf_2 ) : '';
                            sectorPerformers.bot_etf_3 ? performers.bot_etfs.push( sectorPerformers.bot_etf_3 ) : '';
                        }

                        {
                            const sectorState: NormalizedSectorMarketState = {
                                dt: dateOfAnalysis,
                                sectorTmiId: h.sector_id,
                                idx: {
                                    log_rtn: h.idx_log_rtn,
                                    val: 0,
                                    delta: 0,
                                    col: normalizeColumn( h.idx_col ),
                                    col_chg_dt: h.dt_last_idx_col,
                                    sig: normalizeSignal( h.idx_sig ),
                                    sig_chg_dt: h.dt_last_idx_sig
                                },
                                bpi: {
                                    val: h.bpi_val,
                                    col: normalizeColumn( h.bpi_col ),
                                    col_chg_dt: h.dt_last_bpi_col,
                                    sig: normalizeSignal( h.bpi_sig ),
                                    sig_chg_dt: h.dt_last_bpi_sig,
                                    stat: null,
                                    stat_chg_dt: ''
                                },
                                p30: {
                                    val: h.p30_val,
                                    col: normalizeColumn( h.p30_col ),
                                    col_chg_dt: h.dt_last_p30_col,
                                    sig: normalizeSignal( h.p30_sig ),
                                    sig_chg_dt: h.dt_last_p30_sig
                                },
                                p10: {
                                    val: h.p10_val,
                                    col: normalizeColumn( h.p10_col ),
                                    col_chg_dt: h.dt_last_p10_col,
                                    sig: normalizeSignal( h.p10_sig ),
                                    sig_chg_dt: ''
                                },
                                rs: {
                                    rnk: h.rs_rank,
                                    log_rtn: h.rs_log_rtn,
                                    val: 0,
                                    col: normalizeColumn( h.rs_col ),
                                    col_chg_dt: h.rs_chg_dt,
                                    sig: normalizeSignal( h.rs_sig ),
                                    sig_chg_dt: h.dt_last_rs_sig
                                },
                                alerts: []
                            };

                            // tmi-buu: composition is deferred until bpi.stat has been
                            // derived (chronological pass below) — composing here decided
                            // the best/worst performer lists against a stat that is
                            // always null at this point.
                            if ( alerts.length > 0 ) {
                                pendingAlerts.set( `${ dateOfAnalysis }|${ h.sector_id }`, { alerts, performers } );
                            }

                            sectorEntities.push( sectorState );
                        }
                    } );

                    pageEntities.push( {
                        dt: dateOfAnalysis,
                        sectors: [ ...sectorEntities ]
                    } );

                    // Clear the list of sector data
                    sectorEntities = [];
                } );

            const t1: number = performance.now();
            logger.info( `Fetching & mapping sector market data took ${ t1 - t0 } ms` );
        }

        rehydrateFromLogReturns( pageEntities, levels );

        // tmi-buu: BPI status pass, then alert composition against the populated
        // state. The bullish/bearish choice for a BPI Status Change alert — which
        // selects best vs. worst performer lists — needs the very status this pass
        // derives. DP-09: the fold runs over the fetched days together with the
        // cached days around them, so a fetched day's previous row is its real
        // previous trading day even when the fetch is not contiguous. DP-21: the fold
        // carries on from the previous page, over this page's days and the cached days
        // up to its last date, including those of a page that failed.
        const fetched = new Set< BpiStatusDay >( pageEntities );
        const fetchedDates = new Set( pageEntities.map( e => e.dt ) );
        const pageNeighbors: NormalizedPersistedSectorsState[] = [];
        const lastPagedDate = pagedDates[ pagedDates.length - 1 ];
        for ( ; nextNeighbor < neighbors.length && neighbors[ nextNeighbor ].dt <= lastPagedDate; nextNeighbor++ ) {
            if ( !fetchedDates.has( neighbors[ nextNeighbor ].dt ) ) {
                pageNeighbors.push( neighbors[ nextNeighbor ] );
            }
        }
        statusContexts = foldBpiStatuses( [ ...pageNeighbors, ...pageEntities ], ( day, sectorState, stat ) => {
            if ( !fetched.has( day ) ) {
                return;
            }

            sectorState.bpi.stat = stat;
            const pending = pendingAlerts.get( `${ day.dt }|${ sectorState.sectorTmiId }` );
            if ( pending ) {
                sectorState.alerts = [ ...composeSectorAlertsForDate( sectorState, pending.alerts, pending.performers ) ];
            }
        }, statusContexts );

        entities.push( ...pageEntities );
        if ( pageEntities.length > 0 && options.onPage ) {
            await options.onPage( pageEntities );
        }

        if ( !response ) {
            options.onPageLost?.( pagedDates );
        }

        if ( response ) {
            consecutiveFailures = 0;
        } else if ( ++consecutiveFailures >= maxConsecutiveFailures ) {
            logger.warn( `[ sector-history-sync-coordinator ] ${ consecutiveFailures } history pages in a row failed; stopping this pass` );
            break;
        }
    }

    return entities;
}

const LOG_RETURN_BASE = 1000;

/** Running sums per sector, so consecutive pages of one fetch compound as a single batch (DP-21). */
type LogReturnLevels = {
    idxRunningSum: Map< number, number >,
    idxPreviousVal: Map< number, number >,
    rsRunningSum: Map< number, number >
};

function createLogReturnLevels(): LogReturnLevels {
    return { idxRunningSum: new Map(), idxPreviousVal: new Map(), rsRunningSum: new Map() };
}

/**
 * Rehydrates stable cumulative values from daily log returns for EW Index and RS.
 * Log returns are additive: val_t = BASE * e^(Σ log_rtn from start to t).
 * Delta is the difference between consecutive rehydrated values.
 * Mutates data in place. Sorts chronologically to ensure correct accumulation order.
 */
export function rehydrateFromLogReturns( data: NormalizedPersistedSectorsState[], levels: LogReturnLevels = createLogReturnLevels() ): void {
    if ( data.length === 0 ) return;

    data.sort( ( a, b ) => a.dt.localeCompare( b.dt ) );

    const { idxRunningSum, idxPreviousVal, rsRunningSum } = levels;

    for ( const state of data ) {
        for ( const sectorState of state.sectors ) {
            const sectorId = sectorState.sectorTmiId;

            // EW Index rehydration
            if ( sectorState.idx ) {
                const sum = ( idxRunningSum.get( sectorId ) ?? 0 ) + sectorState.idx.log_rtn;
                idxRunningSum.set( sectorId, sum );

                const val = LOG_RETURN_BASE * Math.exp( sum );
                const prevVal = idxPreviousVal.get( sectorId );

                sectorState.idx.val = val;
                sectorState.idx.delta = prevVal != null ? val - prevVal : undefined;
                idxPreviousVal.set( sectorId, val );
            }

            // RS rehydration
            const rsSum = ( rsRunningSum.get( sectorId ) ?? 0 ) + sectorState.rs.log_rtn;
            rsRunningSum.set( sectorId, rsSum );
            sectorState.rs.val = LOG_RETURN_BASE * Math.exp( rsSum );
        }
    }
}

export function hydrate( entity: NormalizedPersistedSectorsState, ctx: CompositionContext ): Tuple<string, Map<number, SectorMarketState>> {
    const dateOfAnalysis: string = entity.dt;

    const sectorToStateMap = new Map<number, SectorMarketState>();
    for ( const sectorState of entity.sectors ) {
        const sector = ctx.getSectorByTmiId( sectorState.sectorTmiId );
        if ( typeof sector !== 'undefined' ) {
            sectorToStateMap.set( sectorState.sectorTmiId, {
                dt: dateOfAnalysis,
                sector: sector,
                idx: sectorState.idx,
                bpi: sectorState.bpi,
                p30: sectorState.p30,
                p10: sectorState.p10,
                rs: sectorState.rs,
                alerts: sectorState.alerts
            } );
        } else {
            logger.warn( `The given TMI ID (${ sectorState.sectorTmiId }) did not map to a valid sector object.` );
        }
    }

    return {
        item1: dateOfAnalysis,
        item2: sectorToStateMap
    }
}

type TopAndBottomPerformers = {
    top_stocks: string[],
    bot_stocks: string[],
    top_etfs: string[],
    bot_etfs: string[]
}

export function composeSectorAlertsForDate( eodState: Readonly< NormalizedSectorMarketState >, alerts: SectorAlert[], performers: TopAndBottomPerformers ): Readonly<SectorAlert>[] {
    const workingSectorAlertList: Readonly< SectorAlert >[] = [];
    for ( const alert of alerts ) {
        const alertKey = `${ alert.dt }+${ eodState.sectorTmiId }+${ alert.typeId }`;

        let bullish: boolean = false;
        switch ( alert.typeId ) {
            case SectorAlertTypes.rs_signal:
                bullish = isBullishSignal( eodState.rs.sig?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.rs_direction:
                bullish = isBullishDirection( eodState.rs.col.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.bpi_signal:
                bullish = isBullishSignal( eodState.bpi.sig?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.bpi_direction:
                bullish = isBullishDirection( eodState.bpi.col.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.bpi_status:
                bullish = isBullishStatus( eodState.bpi.stat?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.wk_30_signal:
                bullish = isBullishSignal( eodState.p30.sig?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.wk_30_direction:
                bullish = isBullishDirection( eodState.p30.col.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.wk_10_signal:
                bullish = isBullishSignal( eodState.p10.sig?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.wk_10_direction:
                bullish = isBullishDirection( eodState.p10.col.toLowerCase() ?? '' );
                break;
        }

        // Build the Sector Alert
        workingSectorAlertList.push( {
            id: alertKey,
            dt: alert.dt,
            sector_id: eodState.sectorTmiId,
            typeId: alert.typeId,
            typeName: alert.typeName,
            bullish: bullish,
            bpi: eodState.bpi,
            p30: eodState.p30,
            p10: eodState.p10,
            rs: eodState.rs,
            stocks: bullish ? performers.top_stocks : performers.bot_stocks,
            etfs: bullish ? performers.top_etfs : performers.bot_etfs
        } );
    }

    return workingSectorAlertList;
}

export function isBullishSignal( signal: string ): boolean {
    switch ( signal ) {
        case 'buy':
            return true;
        case 'sell':
        default:
            return false
    }
}

export function isBullishDirection( direction: string ): boolean {
    switch ( direction ) {
        case 'x':
            return true;
        case 'o':
        default:
            return false
    }
}

const OverboughtThreshold = 70.0;
const OversoldThreshold = 30.0;

/**
 * DP-09: the BPI status machine's carried state for one sector: the status it holds (null before
 * its first evaluation, the writer's "---") and the previous row's value, column and signal.
 * Column/signal are lowercase ('x'/'o', 'buy'/'sell') or null when absent or neutral.
 */
export type BpiStatusContext = {
    status: string | null,
    previousFieldPosition: number | null,
    previousColumn: string | null,
    previousSignal: string | null
};

function knownColumn( column: string | null | undefined ): string | null {
    const value = column?.toLowerCase();
    return value === 'x' || value === 'o' ? value : null;
}

function knownSignal( signal: string | null | undefined ): string | null {
    const value = signal?.toLowerCase();
    return value === 'buy' || value === 'sell' ? value : null;
}

// The writer's transition (sentient-streaming analytics-core sector_events.rs next_bpi_status, C# exact):
// strict comparisons, Bull Top tests the PREVIOUS value, and SELL + O (or no column) at 70 or above
// keeps the prior status.
function nextBpiStatus( prevStatus: string | null, prevValue: number | null, value: number, column: string | null, signal: string ): string | null {
    if ( isBullishSignal( signal ) ) {
        if ( column === 'o' ) {
            if ( prevValue !== null && prevValue > OverboughtThreshold && value < OverboughtThreshold ) return BpiStatus.BearAlert;
            if ( prevValue !== null && prevValue > OverboughtThreshold ) return BpiStatus.BullTop;
            return BpiStatus.BullCorrection;
        }
        return BpiStatus.BullConfirmed;
    }

    if ( column === 'x' ) {
        if ( prevValue !== null && prevValue < OversoldThreshold && value > OversoldThreshold ) return BpiStatus.BullAlert;
        if ( value < OverboughtThreshold ) return BpiStatus.BearCorrection;
        return BpiStatus.BullConfirmed;
    }
    if ( value < OverboughtThreshold ) return BpiStatus.BearConfirmed;
    return prevStatus;
}

/**
 * DP-09 (tmi-e7w.9): one step of the writer's BPI status fold for a sector. The status is re-evaluated
 * only when the value is positive and the BPI column or signal flipped against the previous row;
 * otherwise it is retained. The previous value, column and signal advance on every row, and a neutral
 * '---' (WMD-1251) counts as absent, so it is never a flip itself nor the "previous" side of one.
 * The daily re-evaluation this replaces disagreed with the writer on 10% of sector-days.
 */
export function deriveBpiStatus(
    prevCtx: BpiStatusContext | null,
    column: string | null | undefined,
    signal: string | null | undefined,
    fieldPosition: number | null | undefined
): { stat: string | null, nextCtx: BpiStatusContext } {
    const col = knownColumn( column );
    const sig = knownSignal( signal );
    const value = fieldPosition ?? null;
    let status = prevCtx?.status ?? null;

    const columnFlipped = !!col && !!prevCtx?.previousColumn && col !== prevCtx.previousColumn;
    const signalFlipped = !!sig && !!prevCtx?.previousSignal && sig !== prevCtx.previousSignal;
    if ( value !== null && value > 0.0 && sig && ( columnFlipped || signalFlipped ) ) {
        status = nextBpiStatus( status, prevCtx?.previousFieldPosition ?? null, value, col, sig );
    }

    return {
        stat: status,
        nextCtx: { status, previousFieldPosition: value, previousColumn: col, previousSignal: sig }
    };
}

/** The status state a day hands to the next one: its status and its own value, column and signal. */
export function bpiStatusContextAfter( bpi: Pick< NormalizedSectorMarketState[ 'bpi' ], 'stat' | 'val' | 'col' | 'sig' > ): BpiStatusContext {
    return {
        status: bpi.stat ?? null,
        previousFieldPosition: bpi.val ?? null,
        previousColumn: knownColumn( bpi.col ),
        previousSignal: knownSignal( bpi.sig )
    };
}

type BpiStatusDay = Pick< NormalizedPersistedSectorsState, 'dt' | 'sectors' >;

/**
 * Folds the status machine over `history` in date order, per sector, starting from `seed` (or from
 * nothing), and hands each sector-day its status. Returns each sector's state after the last day, so
 * a later fold over the following days can carry on from it.
 */
function foldBpiStatuses(
    history: readonly BpiStatusDay[],
    visit: ( day: BpiStatusDay, sectorState: NormalizedSectorMarketState, stat: string | null ) => void,
    seed?: Map< number, BpiStatusContext >
): Map< number, BpiStatusContext > {
    const contexts = new Map< number, BpiStatusContext >( seed ?? [] );
    const chronological = [ ...history ].sort( ( d1, d2 ) => d1.dt.localeCompare( d2.dt ) );
    for ( const day of chronological ) {
        for ( const sectorState of day.sectors ) {
            const { stat, nextCtx } = deriveBpiStatus(
                contexts.get( sectorState.sectorTmiId ) ?? null,
                sectorState.bpi.col,
                sectorState.bpi.sig,
                sectorState.bpi.val
            );
            contexts.set( sectorState.sectorTmiId, nextCtx );
            visit( day, sectorState, stat );
        }
    }

    return contexts;
}

/**
 * DP-09: re-derives every cached day's BPI status with the writer's fold, correcting in place what an
 * older rule (or a fetch folded without its neighbors) cached. Returns the days whose status changed,
 * ready to persist, and the days that must come back from the server instead: a cached BPI Status alert
 * whose bias changed holds the other performer list, and only the payload has both.
 */
export function reconcileBpiStatuses( history: NormalizedPersistedSectorsState[] ): { changed: NormalizedPersistedSectorsState[], refetchDates: string[] } {
    const changedDays = new Set< BpiStatusDay >();
    const refetchDays = new Set< BpiStatusDay >();

    foldBpiStatuses( history, ( day, sectorState, stat ) => {
        if ( ( sectorState.bpi.stat ?? null ) === stat ) {
            return;
        }

        sectorState.bpi.stat = stat;
        for ( const alert of sectorState.alerts ?? [] ) {
            if ( alert.bpi ) {
                alert.bpi.stat = stat;
            }
            if ( alert.typeId === SectorAlertTypes.bpi_status && alert.bullish !== isBullishStatus( stat?.toLowerCase() ?? '' ) ) {
                refetchDays.add( day );
            }
        }
        changedDays.add( day );
    } );

    return {
        changed: history.filter( day => changedDays.has( day ) && !refetchDays.has( day ) ),
        refetchDates: history.filter( day => refetchDays.has( day ) ).map( day => day.dt )
    };
}

// P&F BPI status convention: a "Bear Correction" is a counter-trend rally within a
// bear market (chart on a P&F sell signal but rising in X's) — the CHANGE into it is
// bullish. Symmetrically, a "Bull Correction" is a pullback within a bull market
// (buy signal, falling in O's) — the change into it is bearish.
function isBullishStatus( status: string ): boolean {
    switch ( status ) {
        case 'bull confirmed':
        case 'bull alert':
        case 'bear correction':
            return true;
        case 'bear confirmed':
        case 'bull correction':
        case 'bull top':
        case 'bear alert':
        default:
            return false;
    }
}